/**
 * Analytics Composable
 *
 * Provides type-safe methods for tracking events via GTM dataLayer.
 * All events are pushed to window.dataLayer for GTM to process.
 *
 * PII Policy: Never include raw email, phone numbers, names, or addresses
 * in event parameters. See getDestinationType() for URL filtering.
 *
 * EC4L exception: trackLeadSubmit may include SHA-256 hashed normalised
 * email + phone under user_data, per Google Enhanced Conversions for Leads.
 * Hashes are not raw PII; the account-level Customer Data Terms cover this.
 */

// Event name constants (matches GA4 recommended events where applicable)
export const AnalyticsEvent = {
  // Lead Events
  LEAD_FORM_START: 'lead_form_start',
  LEAD_FORM_SUBMIT: 'generate_lead', // GA4 recommended event name

  // Service Qualification Events
  SQ_ATTEMPT: 'sq_attempt',
  SQ_RESULT_ELIGIBLE: 'sq_result_eligible',
  SQ_RESULT_INELIGIBLE: 'sq_result_ineligible',

  // Checkout Funnel Events (GA4 ecommerce)
  CHECKOUT_START: 'begin_checkout',
  CHECKOUT_STEP: 'checkout_step',
  PLAN_SELECTED: 'add_to_cart',
  ORDER_SUBMITTED: 'purchase',

  // Payment Events
  PAYMENT_TOKEN_SUCCESS: 'payment_token_success',
  PAYMENT_TOKEN_FAILURE: 'payment_token_failure',

  // Content Events
  CIS_DOWNLOAD: 'file_download',

  // Support Events
  SUPPORT_FORM_START: 'support_form_start',
  SUPPORT_FORM_SUBMIT: 'support_request',

  // Engagement Events
  CTA_CLICK: 'cta_click',
  PHONE_CLICK: 'phone_click',
} as const

export type AnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent]

// Currency for all ecommerce events
const CURRENCY = 'AUD'

// GA4 Ecommerce item structure
interface EcommerceItem {
  item_id: string
  item_name: string
  item_category: string
  item_variant?: string // e.g., speed tier
  price: number
  quantity: number
}

// Form types for lead tracking
type LeadFormType = 'quick_enquiry' | 'contact' | 'quote' | 'demo' | 'consultation'

// Product types for checkout tracking
type ProductType = 'nbn' | 'voip' | 'fibre'

// Destination types for CTA tracking (no PII)
type DestinationType = 'scroll' | 'internal' | 'external' | 'phone'

declare global {
  interface Window {
    dataLayer: Array<Record<string, unknown>>
  }
}

/**
 * Categorize destination URLs to prevent PII leakage
 *
 * Instead of sending actual URLs (which may contain PII in tel:/mailto: links),
 * we only track the destination type: scroll, internal, external, or phone.
 * The actual URL/phone number is never sent to analytics.
 */
function getDestinationType(destination: string | undefined): DestinationType {
  if (!destination) return 'internal'
  if (destination.startsWith('#')) return 'scroll'
  if (destination.startsWith('tel:')) return 'phone'
  if (destination.startsWith('mailto:')) return 'internal' // Treat as internal, don't expose email
  if (destination.startsWith('http') && !destination.includes('metwide.com.au')) {
    return 'external'
  }
  return 'internal'
}

/**
 * Normalise email for EC4L hashing per Google spec: trim, lowercase, and
 * strip dots from the local part for gmail.com / googlemail.com addresses
 * (Google treats `john.doe@gmail.com` and `johndoe@gmail.com` as the same
 * mailbox; pre-hashing demands the matching normalisation).
 */
function normaliseEmail(value: string): string {
  const lower = value.trim().toLowerCase()
  const at = lower.indexOf('@')
  if (at <= 0) return lower
  const domain = lower.slice(at + 1)
  if (domain !== 'gmail.com' && domain !== 'googlemail.com') return lower
  const local = lower.slice(0, at).replace(/\./g, '')
  return `${local}@${domain}`
}

/**
 * Normalise an Australian phone number to E.164. Strips spaces / dashes /
 * parens, converts a leading 0 to +61. Returns empty string if it can't be
 * coerced to a sensible E.164 form so callers skip the user_data field.
 */
function normalisePhoneE164(value: string): string {
  const stripped = value.replace(/[^\d+]/g, '')
  if (!stripped) return ''
  if (stripped.startsWith('+')) return stripped
  if (stripped.startsWith('0')) return `+61${stripped.slice(1)}`
  if (stripped.startsWith('61')) return `+${stripped}`
  return ''
}

/**
 * Web Crypto SHA-256 → lowercase hex. Browser-only; callers must gate on
 * isEnabled (which already implies typeof window !== 'undefined').
 */
async function sha256Hex(value: string): Promise<string> {
  const buf = new TextEncoder().encode(value)
  const digest = await window.crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Build the user_data payload for Enhanced Conversions for Leads. Returns
 * undefined if no usable inputs were provided.
 */
async function buildUserData(input: {
  email?: string
  phone?: string
}): Promise<Record<string, string> | undefined> {
  const out: Record<string, string> = {}
  const email = input.email ? normaliseEmail(input.email) : ''
  const phone = input.phone ? normalisePhoneE164(input.phone) : ''
  if (email) out.sha256_email_address = await sha256Hex(email)
  if (phone) out.sha256_phone_number = await sha256Hex(phone)
  return Object.keys(out).length ? out : undefined
}

/**
 * Build ecommerce item from plan data
 */
function buildEcommerceItem(
  productType: ProductType,
  planName: string,
  price: number,
  speedTier?: string
): EcommerceItem {
  const item: EcommerceItem = {
    item_id: `${productType}-${planName.toLowerCase().replace(/\s+/g, '-')}`,
    item_name: planName,
    item_category: productType.toUpperCase(),
    price,
    quantity: 1,
  }
  if (speedTier) {
    item.item_variant = speedTier
  }
  return item
}

/**
 * Determine if a service/intent string represents a support request
 * rather than a sales lead. Matches support-* intents from useIntentModal.ts.
 */
export function isSupportIntent(service: string | undefined): boolean {
  if (!service) return false
  const s = service.toLowerCase()
  return s === 'support' || s.startsWith('support-') || s === 'support request'
}

export function useAnalytics() {
  const config = useRuntimeConfig()
  const route = useRoute()

  /**
   * Check if GTM is available
   */
  const isEnabled = computed(() => {
    return !!config.public.gtmId && typeof window !== 'undefined'
  })

  /**
   * Push event to dataLayer
   */
  function trackEvent(eventName: AnalyticsEventName | 'page_view', params?: Record<string, unknown>) {
    if (!isEnabled.value) return

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: eventName,
      ...params,
      // Always include page context
      page_path: route.path,
      page_title: document.title,
    })
  }

  /**
   * Track virtual pageview (for SPA navigation)
   * Called on every route change including initial load
   *
   * Includes page_location and page_referrer for GA4 page reports.
   */
  function trackPageview(path?: string) {
    if (!isEnabled.value) return

    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'page_view',
      page_path: path || route.path,
      page_title: document.title,
      page_location: window.location.href,
      page_referrer: document.referrer,
    })
  }

  // --- Lead Events ---

  /**
   * Track lead form interaction started
   */
  function trackLeadFormStart(formType: LeadFormType, serviceCategory?: string) {
    trackEvent(AnalyticsEvent.LEAD_FORM_START, {
      form_type: formType,
      service_category: serviceCategory,
    })
  }

  /**
   * Track lead form submission. Pass userData (raw email + phone from the
   * form) to attach SHA-256 hashed normalised values under user_data per
   * Google EC4L. Caller should await before resetting form state.
   */
  async function trackLeadSubmit(
    formType: LeadFormType,
    serviceCategory?: string,
    userData?: { email?: string; phone?: string }
  ) {
    const user_data = userData ? await buildUserData(userData) : undefined
    trackEvent(AnalyticsEvent.LEAD_FORM_SUBMIT, {
      form_type: formType,
      service_category: serviceCategory,
      ...(user_data ? { user_data } : {}),
    })
  }

  // --- Support Events ---

  /**
   * Track support form interaction started
   */
  function trackSupportFormStart(formType: LeadFormType, serviceCategory?: string) {
    trackEvent(AnalyticsEvent.SUPPORT_FORM_START, {
      form_type: formType,
      service_category: serviceCategory,
    })
  }

  /**
   * Track support form submission
   */
  function trackSupportSubmit(formType: LeadFormType, serviceCategory?: string) {
    trackEvent(AnalyticsEvent.SUPPORT_FORM_SUBMIT, {
      form_type: formType,
      service_category: serviceCategory,
    })
  }

  // --- Service Qualification Events ---

  /**
   * Track service qualification attempt
   */
  function trackSqAttempt(state: string) {
    trackEvent(AnalyticsEvent.SQ_ATTEMPT, {
      address_state: state,
    })
  }

  /**
   * Track service qualification result
   */
  function trackSqResult(eligible: boolean, technologyType?: string, state?: string) {
    const eventName = eligible
      ? AnalyticsEvent.SQ_RESULT_ELIGIBLE
      : AnalyticsEvent.SQ_RESULT_INELIGIBLE

    trackEvent(eventName, {
      address_state: state || '',
      technology_type: technologyType,
      eligible,
    })
  }

  // --- Checkout/Ecommerce Events (GA4 compliant) ---

  /**
   * Track checkout started (GA4 begin_checkout)
   */
  function trackCheckoutStart(productType: ProductType, planName: string, planPrice: number) {
    const items = [buildEcommerceItem(productType, planName, planPrice)]

    trackEvent(AnalyticsEvent.CHECKOUT_START, {
      currency: CURRENCY,
      value: planPrice,
      items,
      product_type: productType,
    })
  }

  /**
   * Track checkout step completion (custom event for funnel analysis)
   */
  function trackCheckoutStep(productType: ProductType, stepNumber: number, stepName: string) {
    trackEvent(AnalyticsEvent.CHECKOUT_STEP, {
      product_type: productType,
      step_number: stepNumber,
      step_name: stepName,
    })
  }

  /**
   * Track plan selection (GA4 add_to_cart)
   */
  function trackPlanSelected(
    productType: ProductType,
    planName: string,
    planPrice: number,
    speedTier?: string
  ) {
    const items = [buildEcommerceItem(productType, planName, planPrice, speedTier)]

    trackEvent(AnalyticsEvent.PLAN_SELECTED, {
      currency: CURRENCY,
      value: planPrice,
      items,
      product_type: productType,
    })
  }

  /**
   * Track order submission (GA4 purchase)
   */
  function trackOrderSubmitted(
    transactionId: string,
    productType: ProductType,
    planName: string,
    orderValue: number,
    addOns?: string[]
  ) {
    const items = [buildEcommerceItem(productType, planName, orderValue)]

    // Add add-ons as separate items if present
    if (addOns?.length) {
      addOns.forEach((addOn) => {
        items.push({
          item_id: `addon-${addOn.toLowerCase().replace(/\s+/g, '-')}`,
          item_name: addOn,
          item_category: 'Add-on',
          price: 0, // Add-on prices bundled in order_value
          quantity: 1,
        })
      })
    }

    trackEvent(AnalyticsEvent.ORDER_SUBMITTED, {
      transaction_id: transactionId,
      currency: CURRENCY,
      value: orderValue,
      items,
      product_type: productType,
    })
  }

  // --- Payment Events ---

  /**
   * Track successful payment tokenization
   */
  function trackPaymentTokenSuccess(productType: ProductType) {
    trackEvent(AnalyticsEvent.PAYMENT_TOKEN_SUCCESS, {
      product_type: productType,
    })
  }

  /**
   * Track failed payment tokenization
   */
  function trackPaymentTokenFailure(productType: ProductType, errorCode: string) {
    trackEvent(AnalyticsEvent.PAYMENT_TOKEN_FAILURE, {
      product_type: productType,
      error_code: errorCode,
    })
  }

  // --- Engagement Events ---

  /**
   * Track CTA click
   * Note: destination URLs are categorized, not stored (PII protection)
   */
  function trackCtaClick(text: string, location: string, destination?: string) {
    trackEvent(AnalyticsEvent.CTA_CLICK, {
      cta_text: text,
      cta_location: location,
      destination_type: getDestinationType(destination),
    })
  }

  /**
   * Track phone number click
   * Note: Phone number itself is NOT tracked (PII). Only location is tracked.
   */
  function trackPhoneClick(location: string) {
    trackEvent(AnalyticsEvent.PHONE_CLICK, {
      cta_location: location,
    })
  }

  /**
   * Track CIS download
   */
  function trackCisDownload(planName: string, planType: 'nbn' | 'voip') {
    trackEvent(AnalyticsEvent.CIS_DOWNLOAD, {
      file_name: `CIS-${planType}-${planName}.pdf`,
      file_extension: 'pdf',
      link_text: 'Download CIS',
    })
  }

  return {
    isEnabled,
    trackEvent,
    trackPageview,
    // Lead
    trackLeadFormStart,
    trackLeadSubmit,
    // Support
    trackSupportFormStart,
    trackSupportSubmit,
    // SQ
    trackSqAttempt,
    trackSqResult,
    // Checkout (GA4 ecommerce)
    trackCheckoutStart,
    trackCheckoutStep,
    trackPlanSelected,
    trackOrderSubmitted,
    // Payment
    trackPaymentTokenSuccess,
    trackPaymentTokenFailure,
    // Engagement
    trackCtaClick,
    trackPhoneClick,
    trackCisDownload,
  }
}
